import React from 'react';
import Link from 'next/link';
import Image from 'next/image';

// Define SVG Icons as components or constants for reusability
const TwitterIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
  </svg>
);

const DiscordIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M21.539 5.47L18.24 2.097A1.17 1.17 0 0017.231 2H6.77a1.17 1.17 0 00-.99.097L2.46 5.47a1.21 1.21 0 00-.403.87V18.13a1.18 1.18 0 001.177 1.17h3.489l.34-1.528a.87.87 0 01.84-.642h5.214a.87.87 0 01.84.642l.34 1.528h3.489a1.18 1.18 0 001.177-1.17V6.34a1.21 1.21 0 00-.402-.87zM8.678 14.44a1.76 1.76 0 01-1.754-1.76c0-.977.787-1.77 1.754-1.77s1.754.793 1.754 1.77a1.76 1.76 0 01-1.754 1.76zm6.644 0a1.76 1.76 0 01-1.754-1.76c0-.977.787-1.77 1.754-1.77s1.754.793 1.754 1.77a1.76 1.76 0 01-1.754 1.76z"/>
  </svg>
);

const FooterLink = ({ href, children, isExternal = false }: { href: string; children: React.ReactNode; isExternal?: boolean }) => {
  const commonClasses = "text-gray-400 hover:text-gray-200 transition-colors duration-200 text-sm block py-1"; // Added text-sm and py-1

  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={commonClasses}>
        {children}
      </a>
    );
  }

  return (
    <Link href={href} className={commonClasses}>
      {children}
    </Link>
  );
};

const FooterHeader = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-sm font-semibold text-gray-200 mb-3">{children}</h3> // Adjusted styling
);

const Footer = () => {
  // Replace with your actual Discord invite link
  const discordInviteLink = "https://discord.gg/MqdzdRr6DP";

  return (
    <footer className="w-full py-8 bg-white dark:bg-[#0b0b0c] mt-auto text-gray-600 dark:text-gray-300"> {/* Updated background and text colors */}
      <div className="container mx-auto px-12">

        {/* Top Section: Multi-column Layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 mb-8"> {/* Grid layout */}

          {/* Column 1: Brand/Logo (Optional) */}
          <div>
             {/* You can add your logo here using <Image /> */}
             {/* <Image src="/path/to/logo.svg" alt="Saved.gg Logo" width={100} height={24} /> */}
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Saved</h3>
             {/* Optional: Add a short description or tagline */}
             <p className="text-sm text-gray-500 dark:text-gray-400">Your all-in-one AI clipper.</p>
          </div>

          {/* Column 2: Product */}
          <div>
            <FooterHeader>Product</FooterHeader>
            <FooterLink href="/pricing">Pricing</FooterLink>
            <FooterLink href="/creators">Bounties</FooterLink>
            <FooterLink href="/about">About</FooterLink>
             {/* Add other relevant product links */}
          </div>

          {/* Column 3: Resources */}
          <div>
            <FooterHeader>Resources</FooterHeader>
            <FooterLink href="https://blog.saved.gg" isExternal>Blog</FooterLink>
            <FooterLink href={discordInviteLink} isExternal>Discord</FooterLink>
            <FooterLink href="/enterprise">Enterprise</FooterLink>
      
          </div>

          {/* Column 4: Legal */}
          <div>
            <FooterHeader>Legal</FooterHeader>
            <FooterLink href="/terms">Terms of Service</FooterLink>
            <FooterLink href="/privacy">Privacy Policy</FooterLink>
          </div>

        </div>

        {/* Separator Line */}
        <hr className="border-t border-gray-200 dark:border-white/10 my-6" /> {/* Adjusted margin */}

        {/* Bottom Section: Copyright and Social Links */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
          {/* Copyright Text */}
          <div className="text-gray-500 dark:text-gray-400 text-center md:text-left order-last md:order-first">
            © {new Date().getFullYear()} Kudzu Media Inc. All Rights Reserved.
          </div>

          {/* Social Links */}
          <div className="flex items-center gap-5">
            <a
              href="https://twitter.com/savedggclips"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Twitter profile for Saved.gg"
              className="text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
            >
              <TwitterIcon />
            </a>
            <a
              href="https://discord.gg/MqdzdRr6DP"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 text-sm font-bold whitespace-nowrap flex items-center gap-2"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
            </a>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;